import type { MenuOptionType } from "@dexteel/mesf-core";
import { useContextMenuMESF, useHasPermission } from "@dexteel/mesf-core";
import {
  Comment,
  Delete as DeleteIcon,
  Memory as MemoryIcon,
} from "@mui/icons-material";
import { type MouseEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { StrandLoss } from "../../../../../controls/ganttAsset/types/strand-loss";
import { ConfirmationDialogRaw } from "../../../../../controls/modals/ConfirmationDialogRaw";
import { UpsertJustifyStrandLoss } from "../../../../caster/strand-losses-manager/components/modals/UpsertJustifyStrandLoss";
import { useDeleteStrandLossMutation } from "../../../../caster/strand-losses-manager/hooks/mutations/useStrandLossesManagerMutations";
import { useStrandAssetsQuery } from "../../../../caster/strand-losses-manager/hooks/queries/useStrandAssetsQuery";
import { STRAND_LOSS_MODAL } from "../../../../caster/strand-losses-manager/hooks/useStrandLossesOptionFunctions";
import type { StrandAssetScope } from "../../../../caster/strand-losses-manager/models/StrandAsset";
import { getStrandLossById } from "../../../../caster/strand-losses-manager/repositories/StrandLossesManagerRepository";
import { manualStrandLossDeleteMessage } from "./deleteStrandLoss";

const MENU_ID = "ganttSectionStrandLossMenu";
const MANAGER_PERMISSION = "ui.navbar.caster.strand-losses-manager";

const DELETION_PHASE = {
  IDLE: "idle",
  CONFIRMING: "confirming",
  DELETING: "deleting",
} as const;

type DeletionPhase = (typeof DELETION_PHASE)[keyof typeof DELETION_PHASE];

interface PendingDeletion {
  id: number;
  start: Date;
}

interface LossModalProps {
  selectedLoss: StrandLoss | null;
  scope: StrandAssetScope | undefined;
  onHide: () => void;
  onSuccess: () => void;
}

export const GanttStrandLossModal = ({
  selectedLoss,
  scope,
  onHide,
  onSuccess,
}: LossModalProps) => {
  if (!selectedLoss || !scope) return null;

  return (
    <UpsertJustifyStrandLoss
      showModal={STRAND_LOSS_MODAL.JUSTIFY}
      onHide={onHide}
      onSuccess={onSuccess}
      notifyOnSuccess={false}
      strandLossId={selectedLoss.DelayId}
      parentAssetId={scope.parentAssetId}
      strandAssets={scope.strands}
    />
  );
};

export const useGanttStrandLossActions = (
  onError: (message: string) => void,
  onNotify: (message: string) => void,
) => {
  const { showContextMenu, registerConfig } = useContextMenuMESF();
  const hasPermission = useHasPermission();
  const navigate = useNavigate();
  const deleteMutation = useDeleteStrandLossMutation();
  const [selectedLoss, setSelectedLoss] = useState<StrandLoss | null>(null);
  const [pendingDeletion, setPendingDeletion] =
    useState<PendingDeletion | null>(null);
  const deletionPhaseRef = useRef<DeletionPhase>(DELETION_PHASE.IDLE);
  const { data: scope } = useStrandAssetsQuery((error) => {
    onError(error.message);
    setSelectedLoss(null);
  }, selectedLoss !== null);
  const isSelectedLossInScope =
    !!selectedLoss &&
    !!scope?.strands.some((asset) => asset.AssetId === selectedLoss.AssetId);

  useEffect(() => {
    if (!selectedLoss || !scope || isSelectedLossInScope) return;
    onError("The Strand Loss asset is not in the STRANDS asset scope");
    setSelectedLoss(null);
  }, [selectedLoss, scope, isSelectedLossInScope, onError]);

  const openJustifyLoss = (loss: StrandLoss) => {
    if (!hasPermission(MANAGER_PERMISSION)) return;
    setSelectedLoss(loss);
  };

  const handleSeeInManager = async (loss: StrandLoss) => {
    if (!hasPermission(MANAGER_PERMISSION)) return;
    try {
      const response = await getStrandLossById(loss.DelayId);
      if (!response.ok)
        throw new Error(
          response.message ?? "Failed to open Strand Loss in Manager",
        );
      const shiftId = response.data?.ShiftId;
      if (shiftId != null && Number.isInteger(shiftId) && shiftId > 0) {
        navigate(
          `/strand-losses-manager/${shiftId}?strandLossId=${loss.DelayId}`,
        );
      } else {
        navigate("/strand-losses-manager");
      }
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : "Failed to open Strand Loss in Manager",
      );
    }
  };

  const handleDelete = (loss: StrandLoss) => {
    if (
      loss.IsManual !== true ||
      !hasPermission(MANAGER_PERMISSION) ||
      deletionPhaseRef.current !== DELETION_PHASE.IDLE
    )
      return;
    deletionPhaseRef.current = DELETION_PHASE.CONFIRMING;
    setPendingDeletion({ id: loss.DelayId, start: loss.Start });
  };

  const handleDeleteConfirmation = async (value?: string) => {
    if (
      deletionPhaseRef.current !== DELETION_PHASE.CONFIRMING ||
      !pendingDeletion
    )
      return;
    setPendingDeletion(null);
    if (value !== "OK") {
      deletionPhaseRef.current = DELETION_PHASE.IDLE;
      return;
    }
    const confirmedDeletion = pendingDeletion;
    deletionPhaseRef.current = DELETION_PHASE.DELETING;
    try {
      await deleteMutation.mutateAsync(confirmedDeletion.id);
      onNotify("Strand Loss deleted successfully");
    } catch (error) {
      onError(
        error instanceof Error ? error.message : "Failed to delete Strand Loss",
      );
    } finally {
      deletionPhaseRef.current = DELETION_PHASE.IDLE;
    }
  };

  const getOptions = (loss: StrandLoss): MenuOptionType[] => {
    const canManage = hasPermission(MANAGER_PERMISSION);
    const canDelete =
      canManage && deletionPhaseRef.current === DELETION_PHASE.IDLE;
    return [
      {
        name: "Justify Loss",
        key: "justify_strand_loss",
        icon: <Comment />,
        disabled: !canManage,
        onClick: () => openJustifyLoss(loss),
      },
      {
        name: "See in Manager",
        key: "see_strand_loss_in_manager",
        icon: <MemoryIcon />,
        disabled: !canManage,
        onClick: () => handleSeeInManager(loss),
      },
      {
        name: "Delete Strand Loss",
        key: "delete_strand_loss",
        icon: <DeleteIcon />,
        disabled: !canDelete || loss.IsManual !== true,
        onClick: () => handleDelete(loss),
      },
    ];
  };
  const optionsRef = useRef(getOptions);

  useEffect(() => {
    optionsRef.current = getOptions;
  });

  useEffect(() => {
    registerConfig({
      id: MENU_ID,
      getOptions: (loss: StrandLoss) => optionsRef.current(loss),
    });
  }, [registerConfig]);

  const onContextMenu = (event: MouseEvent<Element>, loss: StrandLoss) => {
    if (!hasPermission(MANAGER_PERMISSION)) return;
    showContextMenu(event, loss, MENU_ID);
  };

  const onSuccess = () => onNotify("Strand Loss was justified successfully");

  return {
    onContextMenu,
    onDoubleClick: openJustifyLoss,
    modal: {
      selectedLoss:
        isSelectedLossInScope && hasPermission(MANAGER_PERMISSION)
          ? selectedLoss
          : null,
      scope,
      onHide: () => setSelectedLoss(null),
      onSuccess,
    },
    deletionDialog: (
      <ConfirmationDialogRaw
        title="Confirmation"
        message={
          pendingDeletion
            ? manualStrandLossDeleteMessage(pendingDeletion.start)
            : ""
        }
        open={pendingDeletion !== null}
        onClose={(value) => {
          void handleDeleteConfirmation(value);
        }}
      />
    ),
  };
};
